/**
 * Status style utilities for LLM Config feature
 *
 * Provides visual styling (colours, icons, labels) for provider and model
 * statuses. All maps include a FALLBACK so unknown statuses coming from the
 * API render gracefully instead of crashing the UI.
 *
 * The API is the source of truth for which statuses exist — the frontend
 * only maps *known* ones to specific colours and falls back nicely.
 */

import { CheckCircle, XCircle, AlertTriangle, HelpCircle, type LucideIcon } from 'lucide-react';

// =============================================================================
// Provider Status Styles
// =============================================================================

export interface ProviderStatusStyle {
  /** Tailwind background classes for the badge */
  bg: string;
  /** Tailwind text classes for the badge */
  text: string;
  /** Dot colour class (for compact indicators) */
  dot: string;
  /** i18n key for the status label (e.g. 'admin.llmConfig.providerStatus.healthy') */
  labelKey: string;
}

/**
 * Known provider status styles.
 * If the API returns a status not listed here the fallback is used automatically.
 */
const PROVIDER_STATUS_MAP: Record<string, ProviderStatusStyle> = {
  healthy: {
    bg: 'bg-green-100 dark:bg-green-900/30',
    text: 'text-green-700 dark:text-green-400',
    dot: 'bg-green-500',
    labelKey: 'admin.llmConfig.providerStatus.healthy',
  },
  degraded: {
    bg: 'bg-yellow-100 dark:bg-yellow-900/30',
    text: 'text-yellow-700 dark:text-yellow-400',
    dot: 'bg-yellow-500',
    labelKey: 'admin.llmConfig.providerStatus.degraded',
  },
  unhealthy: {
    bg: 'bg-red-100 dark:bg-red-900/30',
    text: 'text-red-700 dark:text-red-400',
    dot: 'bg-red-500',
    labelKey: 'admin.llmConfig.providerStatus.unhealthy',
  },
  configuring: {
    bg: 'bg-blue-100 dark:bg-blue-900/30',
    text: 'text-blue-700 dark:text-blue-400',
    dot: 'bg-blue-500',
    labelKey: 'admin.llmConfig.providerStatus.configuring',
  },
  unconfigured: {
    bg: 'bg-gray-100 dark:bg-gray-700',
    text: 'text-gray-600 dark:text-gray-400',
    dot: 'bg-gray-400',
    labelKey: 'admin.llmConfig.providerStatus.unconfigured',
  },
  suspended: {
    bg: 'bg-orange-100 dark:bg-orange-900/30',
    text: 'text-orange-700 dark:text-orange-400',
    dot: 'bg-orange-500',
    labelKey: 'admin.llmConfig.providerStatus.suspended',
  },
};

const PROVIDER_STATUS_FALLBACK: ProviderStatusStyle = {
  bg: 'bg-gray-100 dark:bg-gray-700',
  text: 'text-gray-600 dark:text-gray-400',
  dot: 'bg-gray-400',
  labelKey: '', // filled dynamically
};

/**
 * Get visual style for a provider status string.
 * Unknown statuses get a neutral gray style with a dynamic i18n key.
 */
export function getProviderStatusStyle(status: string): ProviderStatusStyle {
  const known = PROVIDER_STATUS_MAP[status];
  if (known) return known;

  // Fallback: use a dynamic key — callers should fall back to capitalised raw value
  return {
    ...PROVIDER_STATUS_FALLBACK,
    labelKey: `admin.llmConfig.providerStatus.${status}`,
  };
}

// =============================================================================
// Model Status Styles
// =============================================================================

export interface ModelStatusStyle {
  icon: LucideIcon;
  className: string;
  labelKey: string;
}

const MODEL_STATUS_MAP: Record<string, ModelStatusStyle> = {
  available: { icon: CheckCircle, className: 'text-green-500', labelKey: 'admin.llmConfig.modelStatus.available' },
  unavailable: { icon: XCircle, className: 'text-red-500', labelKey: 'admin.llmConfig.modelStatus.unavailable' },
  deprecated: { icon: AlertTriangle, className: 'text-yellow-500', labelKey: 'admin.llmConfig.modelStatus.deprecated' },
};

const MODEL_STATUS_FALLBACK: ModelStatusStyle = {
  icon: HelpCircle,
  className: 'text-gray-400',
  labelKey: '',
};

/**
 * Get visual style for a model status string.
 * Unknown statuses get a neutral gray question-mark icon.
 */
export function getModelStatusStyle(status: string): ModelStatusStyle {
  const known = MODEL_STATUS_MAP[status];
  if (known) return known;

  return {
    ...MODEL_STATUS_FALLBACK,
    labelKey: `admin.llmConfig.modelStatus.${status}`,
  };
}
